import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { GUEST_ONLY_ROUTES } from "@/constants/routes";
import { queryClient } from "@/lib/query-client";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/lib/token-storage";
import { store } from "@/store";
import { clearCredentials } from "@/store/auth/authSlice";
import { ApiError, type ApiErrorResponse, type ApiSuccessResponse } from "@/types/api";

const REFRESH_URL = "/auth/refresh";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _isRetry?: boolean;
}

/**
 * Everything `httpClient` (src/lib/axios.ts) does beyond being a
 * configured Axios instance lives here, kept separate so "what the client
 * is" and "what happens on every request/response" aren't one file:
 *
 * 1. Attach the access token (`Authorization: Bearer <token>`, per
 *    docs/architecture/authentication.md) on every request.
 * 2. On a 401 from anything OTHER than the refresh call itself: attempt one
 *    silent `POST /auth/refresh` (the browser sends the httpOnly refresh
 *    cookie automatically — see axios.ts's `withCredentials`), store the
 *    new access token, and retry the original request exactly once
 *    (`_isRetry` guards against a refresh loop). This is the refresh
 *    rotation earlier drafts of this file deliberately deferred — there
 *    was no `/auth/refresh` endpoint to call yet. There is now.
 * 3. If refresh itself fails (refresh cookie missing/expired/revoked):
 *    clear the token, reset `state.auth` to `guest`, clear the TanStack
 *    Query cache, and send the user to log in.
 * 4. Normalize every remaining failure into one `ApiError` shape
 *    (src/types/api.ts) so calling code never branches on server-error-
 *    payload vs. timeout vs. dropped connection.
 */
export function registerInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorResponse>) => {
      const status = error.response?.status ?? null;
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      const isRefreshCall = originalRequest?.url?.includes(REFRESH_URL) ?? false;

      if (status === 401 && originalRequest && !originalRequest._isRetry && !isRefreshCall) {
        originalRequest._isRetry = true;
        try {
          const refreshResponse =
            await client.post<ApiSuccessResponse<{ accessToken: string }>>(REFRESH_URL);
          const newAccessToken = refreshResponse.data.data.accessToken;
          setAccessToken(newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch {
          handleSessionExpired();
          return Promise.reject(
            new ApiError("Your session has expired. Please log in again.", { status: 401 }),
          );
        }
      }

      if (status === 401) {
        handleSessionExpired();
      }

      const payload = error.response?.data;

      if (payload && payload.success === false) {
        return Promise.reject(
          new ApiError(payload.message, { status, fieldErrors: payload.errors }),
        );
      }

      if (error.code === "ECONNABORTED") {
        return Promise.reject(
          new ApiError("The request timed out. Check your connection and try again.", {
            status: null,
          }),
        );
      }

      if (!error.response) {
        return Promise.reject(
          new ApiError("Unable to reach the server. Check your connection and try again.", {
            status: null,
          }),
        );
      }

      return Promise.reject(new ApiError("Something went wrong. Please try again.", { status }));
    },
  );
}

function handleSessionExpired(): void {
  clearAccessToken();
  store.dispatch(clearCredentials());
  queryClient.clear();
  if (typeof window !== "undefined" && window.location.pathname !== GUEST_ONLY_ROUTES.login) {
    window.location.assign(GUEST_ONLY_ROUTES.login);
  }
}
